import React from 'react';
import PlacesAutocomplete from 'react-places-autocomplete';

function AddressAutocomplete({ value, onChange, placeholder = 'Enter address', dark = false }) {
  // Use the new Place API instead of deprecated PlacesService
  const handleSelect = async (selectedAddress, placeId) => {
    if (!placeId) {
      onChange(selectedAddress);
      return;
    }

    try {
      // Use the new Place API (recommended for new customers)
      const { Place } = await window.google.maps.importLibrary('places');
      const place = new Place({
        id: placeId
      });

      // New Places API (Place class): field mask uses camelCase, not legacy snake_case.
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress', 'location'],
      });

      const components = place.addressComponents || [];
      let zipCode = '', state = '', city = '', streetNumber = '', streetName = '';

      const longText = (c) => c.longText ?? c.long_name ?? '';
      const shortText = (c) => c.shortText ?? c.short_name ?? '';

      components.forEach((component) => {
        const types = component.types || [];
        if (types.includes('postal_code')) zipCode = longText(component);
        if (types.includes('administrative_area_level_1')) state = shortText(component);
        if (types.includes('locality')) city = longText(component);
        if (types.includes('street_number')) streetNumber = longText(component);
        if (types.includes('route')) streetName = longText(component);
      });

      const loc = place.location;
      let lat = null;
      let lng = null;
      if (loc) {
        if (typeof loc.lat === 'function') {
          lat = loc.lat();
          lng = loc.lng();
        } else if (typeof loc.lat === 'number') {
          lat = loc.lat;
          lng = loc.lng;
        }
      }

      onChange({
        address: selectedAddress,
        formattedAddress: place.formattedAddress || selectedAddress,
        zipCode,
        state,
        city,
        streetNumber,
        streetName,
        lat,
        lng,
      });
    } catch (error) {
      console.error('Error fetching place details:', error);
      // Fallback: just use the text the user selected
      onChange(selectedAddress);
    }
  };

  return (
    <PlacesAutocomplete
      value={value}
      onChange={onChange}
      onSelect={handleSelect}
      searchOptions={{
        componentRestrictions: { country: 'us' } // Restrict to US, adjust if needed
      }}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div style={{ position: 'relative' }}>
          <input
            {...getInputProps({
              placeholder: placeholder,
              className: 'input-field',
              style: {
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                border: dark ? '1px solid #475569' : '1px solid #ccc',
                borderRadius: '6px',
                boxSizing: 'border-box',
                ...(dark ? { color: '#e2e8f0', backgroundColor: '#0f172a' } : {}),
              }
            })}
          />
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: dark ? '#1e293b' : '#fff',
              border: dark ? '1px solid #475569' : '1px solid #ccc',
              borderTop: 'none',
              borderRadius: '0 0 6px 6px',
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 1000
            }}
          >
            {loading && (
              <div style={{ padding: '10px', color: dark ? '#94a3b8' : '#666' }}>Loading suggestions...</div>
            )}
            {suggestions.map(suggestion => {
              const className = suggestion.active ? 'suggestion-item-active' : 'suggestion-item';
              const style = dark
                ? suggestion.active
                  ? {
                      backgroundColor: '#312e81',
                      cursor: 'pointer',
                      padding: '10px 12px',
                      borderBottom: '1px solid #334155',
                      color: '#e0e7ff',
                    }
                  : {
                      backgroundColor: '#0f172a',
                      cursor: 'pointer',
                      padding: '10px 12px',
                      borderBottom: '1px solid #334155',
                      color: '#e2e8f0',
                    }
                : suggestion.active
                  ? {
                      backgroundColor: '#e8f4f8',
                      cursor: 'pointer',
                      padding: '10px 12px',
                      borderBottom: '1px solid #eee',
                    }
                  : {
                      backgroundColor: '#ffffff',
                      cursor: 'pointer',
                      padding: '10px 12px',
                      borderBottom: '1px solid #eee',
                    };
              return (
                <div
                  {...getSuggestionItemProps(suggestion, { className, style })}
                  key={suggestion.placeId}
                >
                  <span style={{ fontSize: '13px' }}>{suggestion.description}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </PlacesAutocomplete>
  );
}

export default AddressAutocomplete;
